import { logger } from '@magicschool/logger';
import { BUCKET_KNOWLEDGE_BASE } from '@magicschool/supabase/storage-buckets';
import { clientSupabase } from '@/util/supabase';

/**
 * EAS-365 (4b): upload files for a MIGRATED custom chatbot into its own CDS knowledge base, via the
 * per-tool documents route's 3-step signed-URL flow (GET upload-url -> PUT to storage -> POST confirm).
 *
 * Mirrors the callback shape of the legacy {@link uploadFiles} so the store can swap implementations
 * behind the migration gate without changing its own state handling. Only reached when the bot is
 * admitted + the flag is on; the legacy OpenAI/TUS path stays the default for everything else.
 */
export const uploadFilesToCds = async ({
  files,
  customizationId,
  onError,
  onProgress,
  onSuccess,
}: {
  files: File[];
  customizationId: string;
  onError?: (file: File, error: string) => void;
  onProgress?: (file: File, progress: number) => void;
  onSuccess?: (file: File, fileId: string) => void;
}) => {
  const results = await Promise.allSettled<{ fileName: string; fileId?: string; error?: string }>(
    files.map(async (file) => {
      try {
        // 1. Signed upload URL scoped to this bot's KB path.
        const params = new URLSearchParams({ action: 'upload-url', fileName: file.name, fileSize: String(file.size), mimeType: file.type });
        const urlRes = await fetch(`/api/tool-customizations/${customizationId}/documents?${params.toString()}`);
        if (!urlRes.ok) {
          const msg = `Failed to get upload URL for ${file.name} (${urlRes.status})`;
          onError?.(file, msg);
          return { fileName: file.name, error: msg };
        }
        const { token, storagePath } = (await urlRes.json()) as { signedUrl: string; token: string; storagePath: string };

        // 2. PUT the bytes to storage via the signed token.
        const { error: uploadError } = await clientSupabase.storage.from(BUCKET_KNOWLEDGE_BASE).uploadToSignedUrl(storagePath, token, file);
        if (uploadError) {
          onError?.(file, uploadError.message);
          return { fileName: file.name, error: uploadError.message };
        }
        onProgress?.(file, 100);

        // 3. Confirm -> creates the document row + triggers ingestion.
        const confirmRes = await fetch(`/api/tool-customizations/${customizationId}/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storagePath, fileName: file.name, fileSize: file.size, mimeType: file.type }),
        });
        if (!confirmRes.ok) {
          const msg = `Failed to confirm upload for ${file.name} (${confirmRes.status})`;
          onError?.(file, msg);
          return { fileName: file.name, error: msg };
        }
        const { id } = (await confirmRes.json()) as { id: string };
        onSuccess?.(file, id);
        return { fileName: file.name, fileId: id };
      } catch (error) {
        const msg = error instanceof Error ? error.message : `Upload failed for ${file.name}`;
        logger.error(`CDS upload failed for ${file.name}`, error);
        onError?.(file, msg);
        return { fileName: file.name, error: msg };
      }
    }),
  );

  return { queued: false, results };
};
